import { useCallback, useMemo } from 'react';

import { UseFormyValidatorForms } from '../../types/form';
import { useSelectionValidationForm } from '../selectors/useSelectionValidationForm';
import { ScrollIntoViewParams, ScrollParams } from './scrolling.types';
import { useFormScroll } from './useFormScroll';

/**
 * Enables scrolling to a form or forms inputs by the form Id
 *
 * @param formId - The id of the form to scroll to
 * @param scrollParams - The parameters to pass to the scrollIntoView function
 * @returns The scroll provider
 */
const useScrollToForm = <
  TForms extends UseFormyValidatorForms,
  TKey extends keyof TForms,
  TForm extends TForms[TKey],
>(
  formId: TKey,
  scrollParams?: ScrollParams
) => {
  const { scrollToNode, ...scrollProvider } = useFormScroll(scrollParams);
  const form = useSelectionValidationForm<TForms, TKey>(formId) as TForm | undefined;

  /**
   * Scrolls to the given form
   */
  const scrollToForm = useCallback(
    (args: ScrollIntoViewParams = {}) => {
      if (form?.formRef?.current) {
        scrollToNode(form?.formRef?.current, args);
      }
    },
    [form?.formRef, scrollToNode]
  );

  /**
   * Scrolls to the given input from within a form
   */
  const scrollToInput = useCallback(
    <TField extends keyof TForm['values']>(inputId: TField, args: ScrollIntoViewParams = {}) => {
      if (!form) return;
      // Get the input element
      const inputNode = form?.getInputNode(inputId);

      if (inputNode) {
        scrollToNode(inputNode, args);
      }
    },
    [form, scrollToNode]
  );

  const _scrollProvider = useMemo(
    () => ({
      ...scrollProvider,
      scrollToForm,
      scrollToInput,
    }),
    [scrollProvider, scrollToForm, scrollToInput]
  );

  return _scrollProvider;
};

export { useScrollToForm };
